import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiTable } from './components';
import styles from './ExternalApi.module.css';
import { useAuthState, useAuthActions, AuthProvider } from './AuthContext';
import { useApiClient } from './useApiClient';
import { FormComponent } from './components/form';

function ExternalApiBody() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthState();
  const { connect, handleCallback } = useAuthActions();
  const { getWithAuth, deleteWithAuth } = useApiClient();

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [pageError, setPageError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const error = searchParams.get("error");

  const didFetchRef = useRef(false);

  // --- Refresh existing API credentials ---
  const refreshCredentials = async () => {
    setLoading(true);
    try {
      const data = await getWithAuth("/credentials");
      setCredentials(Array.isArray(data.items) ? data.items : data.items ?? []);
    } finally {
      setLoading(false);
    }
  };

  // --- OAuth2 callback hanlding ---
  useEffect(() => {
    if ((!code && !error)) return;

    const run = async () => {
      try {
        if (error) throw new Error(error);

        await handleCallback(code, state);
        navigate("/settings", { replace: true });
      } catch (err) {
        setPageError(err.message);
      }
    };

    run();
  }, [code, state, error, handleCallback, navigate]);

  // --- Loading available credentials ---
  useEffect(() => {
    if (!isAuthenticated) return;
    if (didFetchRef.current) return;

    didFetchRef.current = true;
    refreshCredentials();
  }, [isAuthenticated]);

  // --- Different handlers for buttons ---
  const handleEdit = async (entry) => {
    const results = await getWithAuth(`/credentials/${entry.api_name}`);
    setSelectedEntry(results);
  }
  const handleCancelEdit = () => {
    setSelectedEntry(null);
  }
  const handleSaved = async () => {
    setSelectedEntry(null);
    await refreshCredentials();
  }
  const handleDelete = async (items) => {
    await deleteWithAuth(`/credentials/${items.api_name}`);
    // console.log("Delete selected API:", items.api_name);
    await refreshCredentials();
  }

  // --- Main component body ---
  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        <span className={styles.title}>Existing connections</span>

        {!isAuthenticated ? (
          <button className={styles.addApi} onClick={connect}>
            Reconnect
          </button>
        ) : (
          <button className={styles.addApi}
            onClick={refreshCredentials}
          >
            Refresh credentials
          </button>
        )}

        {pageError && <div>{pageError}</div>}
      </div>

      <div className={styles.bodyContainer}>
        <ApiTable
          items={credentials}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />

        <FormComponent
          selectedEntry={selectedEntry}
          onSaved={handleSaved}
          onCancelEdit={handleCancelEdit}
        />
      </div>
    </div>
  );
};

// --- Provider wrapper ---
function ExternalApi() {
  return (
    <AuthProvider>
      <ExternalApiBody />
    </AuthProvider>
  )
}
export default ExternalApi;